import { ReactNode } from "react";
import { Box } from "@leagueplatform/genesis-core";
import { HEADER_NAV_BAR_DEFAULT_HEIGHT } from "@leagueplatform/solutions-foundation";

const CARD_MAX_WIDTH = "400px";

/**
 * Masonry's loading spinner reserves 80vh, which stretches the card until the
 * node lands. Both selectors match its container — a `StackLayout` div whose
 * child is the spinner, unlike a button's own spinner, which the SDK wraps in a
 * `span` — and they are spelled differently because `:has()` cannot be nested
 * inside another `:has()`: the browser drops the whole rule as invalid.
 */
export const MASONRY_SPINNER_SELECTOR = "div:has(> .GDS-spinner)";
export const MASONRY_SPINNER_PRESENT_SELECTOR = "&:has(div > .GDS-spinner)";

/**
 * Shared modal chrome for Self-Collection confirmation screens
 * (Order Placed, Cancellation, Order Cancelled).
 *
 * - Desktop: gray backdrop + centered white card
 * - Mobile: full-screen white surface, content vertically centered
 *
 * The inner wrapper groups `children` so multi-child content
 * (e.g. icon + Masonry) centers as one unit under the mobile grid.
 */
export const SelfCollectionModalCard = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Box
    css={{
      minHeight: `calc(100vh - ${HEADER_NAV_BAR_DEFAULT_HEIGHT}px)`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "rgba(136, 136, 136, 0.7)",
      padding: "$three",
      "@mobile": {
        background: "$surfaceBackgroundPrimary",
        padding: 0,
        alignItems: "stretch",
      },
    }}
  >
    <Box
      css={{
        width: "100%",
        maxWidth: CARD_MAX_WIDTH,
        background: "$surfaceBackgroundPrimary",
        borderRadius: "$card",
        boxShadow: "$card",
        padding: "$twoAndHalf",
        textAlign: "center",
        "@mobile": {
          boxShadow: "none",
          borderRadius: 0,
          height: `calc(100vh - ${HEADER_NAV_BAR_DEFAULT_HEIGHT}px)`,
          display: "grid",
          placeItems: "center",
          padding: "24px 24px 52px",
        },
        [MASONRY_SPINNER_PRESENT_SELECTOR]: {
          background: "none",
          boxShadow: "none",
          padding: 0,
          alignSelf: "stretch",
          display: "grid",
          placeItems: "center",
          [`& ${MASONRY_SPINNER_SELECTOR}`]: { minHeight: 0 },
        },
      }}
    >
      <Box>{children}</Box>
    </Box>
  </Box>
);
